import { mkdir, readFile, stat, writeFile } from 'fs/promises'
import { dirname, resolve } from 'path'
import { resolveArtifactWritePlan } from './artifact-merge.ts'
import { generateArtifactsFromSnapshot } from './renderer.ts'
import { loadGeneratorConfig } from './config.ts'
import { resolveGeneratorProvider } from './providers.ts'
import { resolveProviderSchemas } from './schema-selection.ts'
import type {
  GeneratedArtifact,
  LoadGeneratorConfigOptions,
  NormalizedAthenaGeneratorConfig,
  RunGeneratorOptions,
  RunGeneratorResult,
  SkippedGeneratedArtifact,
  WrittenGeneratedArtifact,
} from './types.ts'

async function fileExists(path: string): Promise<boolean> {
  try {
    await stat(path)
    return true
  } catch {
    return false
  }
}

async function readExisting(path: string): Promise<string | null> {
  try {
    return await readFile(path, 'utf8')
  } catch {
    return null
  }
}

function policyForArtifact(
  file: GeneratedArtifact,
  config: NormalizedAthenaGeneratorConfig,
): 'always' | NormalizedAthenaGeneratorConfig['output']['artifactWrite']['database'] {
  if (file.kind === 'model' || file.kind === 'schema') {
    return 'always'
  }
  if (file.kind === 'database') {
    return config.output.artifactWrite.database
  }
  return config.output.artifactWrite.registry
}

async function writeArtifacts(
  files: GeneratedArtifact[],
  cwd: string,
  config: NormalizedAthenaGeneratorConfig,
  dryRun: boolean,
): Promise<{
  writtenFiles: string[]
  writtenDetails: WrittenGeneratedArtifact[]
  skippedFiles: SkippedGeneratedArtifact[]
}> {
  const writtenFiles: string[] = []
  const writtenDetails: WrittenGeneratedArtifact[] = []
  const skippedFiles: SkippedGeneratedArtifact[] = []

  for (const file of files) {
    const absolutePath = resolve(cwd, file.path)
    const exists = await fileExists(absolutePath)
    const existingContent = exists ? await readExisting(absolutePath) : null
    const policy = policyForArtifact(file, config)
    const plan = resolveArtifactWritePlan(file, existingContent, policy)

    if (plan.action === 'skip' || plan.action === 'unchanged') {
      skippedFiles.push({
        kind: file.kind,
        path: file.path,
        reason: plan.skipReason ?? 'already-current',
        detail: plan.detail,
        preservedCustom: plan.preservedCustom.length > 0 ? plan.preservedCustom : undefined,
        conflicts: plan.conflicts.length > 0 ? plan.conflicts : undefined,
        lintErrors: plan.lintErrors.length > 0 ? plan.lintErrors : undefined,
      })
      continue
    }

    if (!plan.content || !plan.writeReason) {
      skippedFiles.push({
        kind: file.kind,
        path: file.path,
        reason: 'merge-lint-failed',
        detail: 'merge produced no content',
        lintErrors: plan.lintErrors,
      })
      continue
    }

    if (!dryRun) {
      await mkdir(dirname(absolutePath), { recursive: true })
      await writeFile(absolutePath, plan.content, 'utf8')
    }

    writtenFiles.push(file.path)
    writtenDetails.push({
      kind: file.kind,
      path: file.path,
      reason: plan.writeReason,
      added: plan.added.length > 0 ? plan.added : undefined,
      preservedCustom: plan.preservedCustom.length > 0 ? plan.preservedCustom : undefined,
    })
  }

  return {
    writtenFiles,
    writtenDetails,
    skippedFiles,
  }
}

/**
 * End-to-end generator execution: load config, introspect, render, and optionally write files.
 */
export async function runSchemaGenerator(options: RunGeneratorOptions = {}): Promise<RunGeneratorResult> {
  const cwd = options.cwd ?? process.cwd()
  const configOptions: LoadGeneratorConfigOptions = {
    cwd,
    configPath: options.configPath,
  }

  const { configPath, config } = await loadGeneratorConfig(configOptions)
  const provider = options.provider ?? resolveGeneratorProvider(config.provider, config.experimental)

  const snapshot = await provider.inspect({
    schemas: resolveProviderSchemas(config.provider),
  })

  const generated = generateArtifactsFromSnapshot(snapshot, config)
  const writeResult = await writeArtifacts(
    generated.files,
    cwd,
    config,
    options.dryRun === true,
  )

  return {
    ...generated,
    configPath,
    config,
    writtenFiles: writeResult.writtenFiles,
    writtenDetails: writeResult.writtenDetails,
    skippedFiles: writeResult.skippedFiles,
  }
}
